import { useState } from 'react';
import { Link } from 'react-router-dom';
import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Send, CheckCircle, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTracking, subscribeNewsletter } from '../hooks/useAnalytics';

const NewsletterPage = () => {
    // Track page view
    usePageTracking('Newsletter');
    
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error('Please enter your email address');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await subscribeNewsletter(email);
            if (result.status === 'already_subscribed') {
                toast.info('You are already subscribed to our newsletter');
            } else {
                toast.success('Successfully subscribed to the newsletter!');
            }
            setIsSubscribed(true);
        } catch (error) {
            toast.error('Failed to subscribe. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const pastNewsletters = [
        { 
            month: 'April 2026', 
            title: 'Spring Forward: Leadership & Growth', 
            highlights: ["From the Founder's Desk", 'R&R Saturday Recap', 'New Committee Chairs', 'Brother Spotlight'], 
            link: '/community/newsletter/april-2026',
            isInternal: true
        },
        { 
            month: 'March 2026', 
            title: 'Building Brotherhood: Women\'s History Month', 
            highlights: ["From the Founder's Desk", 'R&R Gathering Recap', 'Brother Spotlight', 'New Committee Roles'], 
            link: '/community/newsletter/march-2026',
            isInternal: true
        },
        { 
            month: 'February 2026', 
            title: 'Love & Healing: Black History Month', 
            highlights: ["From the Founder's Desk", 'R&R Gathering Recap', 'Career Opportunities', 'Meditation'], 
            link: '/community/newsletter/february-2026',
            isInternal: true
        },
    ];

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            <main className="pt-32 pb-24 px-6">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            In The Community
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Monthly Newsletter
                        </h1>
                        <p className="text-[#A0A0A0] text-lg max-w-2xl mx-auto">
                            Stay connected with the latest updates, events, and stories from the Recovery Room community
                        </p>
                    </div>

                    {/* Subscribe Section */}
                    <Card className="bg-gradient-to-br from-[#4A5240] to-[#3D4435] border-[#C9A227]/30 mb-12">
                        <CardContent className="p-8 lg:p-12">
                            {isSubscribed ? (
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-full bg-[#C9A227]/20 flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle className="w-8 h-8 text-[#C9A227]" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-4">
                                        You're Subscribed!
                                    </h2>
                                    <p className="text-[#A0A0A0]">
                                        Thank you for subscribing. You'll receive our next newsletter in your inbox.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                                    <div>
                                        <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 flex items-center justify-center mb-6">
                                            <Mail className="w-7 h-7 text-[#C9A227]" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-4">
                                            Subscribe to Our Newsletter
                                        </h2>
                                        <p className="text-[#A0A0A0] mb-4">
                                            Get monthly updates on events, member spotlights, wellness tips, and community news delivered straight to your inbox.
                                        </p>
                                        <ul className="space-y-2 text-[#A0A0A0] text-sm">
                                            <li className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-[#C9A227]" />
                                                Upcoming events and gatherings
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-[#C9A227]" />
                                                Wellness articles and resources
                                            </li>
                                        </ul>
                                    </div>
                                    <div>
                                        <form onSubmit={handleSubscribe} className="space-y-4">
                                            <Input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Enter your email address"
                                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227] h-12"
                                                data-testid="newsletter-email-input"
                                            />
                                            <Button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold h-12"
                                                data-testid="newsletter-subscribe-btn"
                                            >
                                                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                                                <Send className="w-4 h-4 ml-2" />
                                            </Button>
                                        </form>
                                        <p className="text-[#666] text-xs mt-4 text-center">
                                            We respect your privacy. Unsubscribe at any time.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Newsletters */}
                    <section>
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8 flex items-center gap-3">
                            <FileText className="w-6 h-6 text-[#C9A227]" />
                            Newsletters
                        </h2>
                        <div className="space-y-4">
                            {pastNewsletters.map((newsletter, idx) => (
                                <Card key={idx} className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all">
                                    <CardContent className="p-6">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                            <div>
                                                <p className="text-[#C9A227] text-sm font-medium mb-1">{newsletter.month}</p>
                                                <h3 className="text-white font-semibold text-lg mb-2">{newsletter.title}</h3>
                                                <div className="flex flex-wrap gap-2">
                                                    {newsletter.highlights.map((highlight, hIdx) => (
                                                        <span 
                                                            key={hIdx}
                                                            className="px-2 py-1 bg-[#3D4435] rounded text-xs text-[#A0A0A0]"
                                                        >
                                                            {highlight}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            {newsletter.isInternal ? (
                                                <Link to={newsletter.link}>
                                                    <Button
                                                        variant="outline"
                                                        className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white shrink-0"
                                                    >
                                                        Read Issue
                                                    </Button>
                                                </Link>
                                            ) : (
                                                <a
                                                    href={newsletter.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Button
                                                        variant="outline"
                                                        className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white shrink-0"
                                                    >
                                                        Read Issue
                                                    </Button>
                                                </a>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>
                </div>
            </main>

            <MainFooter />
        </div>
    );
};

export default NewsletterPage;
